'use client';

import { useEffect, useRef, useState } from 'react';
import type { JSX, Ref } from 'react';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { AutocompleteInputChangeReason } from '@mui/material/useAutocomplete';

import {
  ADMIN_SCHEMA_PREFIX_SEARCH_PATH,
  type DisplayConceptRef,
  SCHEMA_PREFIX_SEARCH_PATH,
} from '@/lib/api';
import { searchAdminConceptsByPrefix, searchConceptsByPrefix } from '@/lib/api/generated';

/** Configures one reusable controlled concept-selection field. */
export interface ControlledConceptAutocompleteProps {
  /** Names the text field for assistive technology when no visible label is appropriate. */
  ariaLabel?: string;
  /** Disambiguates the generated autocomplete identifier from sibling fields. */
  instanceId: string;
  /** Selects the credentialed API path used for concept prefix searches. */
  searchPath: string;
  /** Supplies the currently selected known concept. */
  value: DisplayConceptRef | null;
  /** Receives selected known concepts and explicit clearing. */
  onChange: (value: DisplayConceptRef | null) => void;
  /** Labels the actual text field when the surrounding form requires one. */
  label?: string;
  /** Supplies placeholder text when no value is selected. */
  placeholder?: string;
  /** Seeds visible text when a remounted field is carrying unresolved input. */
  initialInputValue?: string;
  /** Prevents input and selection while the containing workflow is pending. */
  disabled?: boolean;
  /** Exposes input edits and MUI change reasons for containing-form validation. */
  onInputChange?: (inputValue: string, reason: AutocompleteInputChangeReason) => void;
  /** Gives navigation-oriented wrappers direct access to the actual input. */
  inputRef?: Ref<HTMLInputElement>;
}

/**
 * Searches concepts while exposing only controlled known-concept selection.
 *
 * Search, debounce, option, and request-order state stay inside this field. The
 * component intentionally has no navigation behavior, global shortcuts, or
 * page-width layout so dialogs and other forms can reuse it safely.
 */
export function ControlledConceptAutocomplete({
  ariaLabel,
  disabled = false,
  initialInputValue,
  inputRef,
  instanceId,
  label,
  onChange,
  onInputChange,
  placeholder,
  searchPath,
  value,
}: ControlledConceptAutocompleteProps): JSX.Element {
  const [inputValue, setInputValue] = useState(initialInputValue ?? value?.display ?? '');
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<DisplayConceptRef[]>([]);
  const previousValue = useRef({ cref: value?.cref, display: value?.display });
  const preserveInitialInput = useRef(Boolean(initialInputValue) && value == null);
  const requestSequence = useRef(0);

  useEffect(() => {
    if (
      previousValue.current.cref !== value?.cref ||
      previousValue.current.display !== value?.display
    ) {
      preserveInitialInput.current = false;
      setInputValue(value?.display ?? '');
      previousValue.current = { cref: value?.cref, display: value?.display };
    }
  }, [value?.cref, value?.display]);

  useEffect(() => {
    requestSequence.current += 1;
    const sequence = requestSequence.current;
    const query = inputValue.trim();
    let controller: AbortController | null = null;

    if (disabled || !query || (value != null && inputValue === value.display)) {
      setLoading(false);
      setOptions([]);
      return undefined;
    }

    setLoading(false);
    const timeoutId = window.setTimeout(async () => {
      controller = new AbortController();
      setLoading(true);
      try {
        const search =
          searchPath === ADMIN_SCHEMA_PREFIX_SEARCH_PATH
            ? searchAdminConceptsByPrefix
            : searchConceptsByPrefix;
        if (
          searchPath !== ADMIN_SCHEMA_PREFIX_SEARCH_PATH &&
          searchPath !== SCHEMA_PREFIX_SEARCH_PATH
        ) {
          throw new Error('Unsupported concept search contract');
        }
        const results = await search(query, { signal: controller.signal });
        if (requestSequence.current === sequence) {
          setOptions(results);
        }
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          return;
        }
        if (requestSequence.current === sequence) {
          setOptions([]);
        }
      } finally {
        if (requestSequence.current === sequence) {
          setLoading(false);
        }
      }
    }, 300);

    return () => {
      window.clearTimeout(timeoutId);
      controller?.abort();
    };
  }, [disabled, inputValue, searchPath, value?.cref, value?.display]);

  return (
    <Autocomplete
      clearOnBlur={false}
      clearOnEscape
      disabled={disabled}
      filterOptions={(results) => results}
      getOptionLabel={(option) => option.display}
      id={instanceId}
      includeInputInList
      inputValue={inputValue}
      isOptionEqualToValue={(option, selected) => option.cref === selected.cref}
      loading={loading}
      loadingText="Searching..."
      noOptionsText="No topics found"
      onChange={(_event, selected) => onChange(selected)}
      onClose={() => setOpen(false)}
      onInputChange={(_event, nextInputValue, reason) => {
        // MUI resets input for a null selected value during mount. Preserve an
        // unresolved seed until the user edits it or a controlled value arrives.
        if (
          preserveInitialInput.current &&
          reason === 'reset' &&
          nextInputValue === '' &&
          value == null
        ) {
          return;
        }
        preserveInitialInput.current = false;
        if (reason === 'input' || reason === 'clear') {
          setOptions([]);
        }
        setInputValue(nextInputValue);
        onInputChange?.(nextInputValue, reason);
      }}
      onOpen={() => setOpen(true)}
      open={open}
      options={options}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          inputRef={inputRef}
          label={label}
          placeholder={placeholder}
          size="small"
          slotProps={{
            htmlInput: {
              ...params.inputProps,
              'aria-label': ariaLabel,
            },
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
          variant="outlined"
        />
      )}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        return (
          <Box component="li" key={option.cref} {...optionProps}>
            <Typography component="div" variant="body1">
              <Typography component="span" sx={{ fontWeight: 'bold' }}>
                {option.display}:
              </Typography>{' '}
              <Typography component="span" color="text.secondary">
                {option.description}
              </Typography>
            </Typography>
          </Box>
        );
      }}
      selectOnFocus={false}
      sx={{
        width: '100%',
        '& .MuiAutocomplete-listbox': {
          maxHeight: 300,
        },
      }}
      value={value}
    />
  );
}
